import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Grid, AgGrid, Flex, toast } from "@jsq/ds/core";
import { LoadingPage } from "~/components/LoadingPage/LoadingPage";
import {
  useGetCompliancePositionsListLazyQuery,
  useUserScopedArenaIdsQuery,
} from "~/graphql/supergraphHooks";
import NoRowsOverlay from "~/pages/Compliance/Common/NoRowsOverlay";
import { toDbId, toGlobalId } from "~/utils/graphql-helpers";
import { useComplianceProfileRefreshTab, useEnableClientLevelPermissions } from "../flags";
import { dashboardContainer } from "../styles_vanilla.css";
import { getColDefinitions } from "./columnDefinition";
import EntitySearchBox from "./EntitySearchBox";
import { EntitySearchOption } from "./EntitySearchBox/types";
import ExportButton from "./ExportButton";
import { useEntityComplianceProgram } from "./hooks";
import { topOptionsContainer } from "./styles_vanilla.css";
import { PositionsTableRowItem } from "./types";
import { rowsFromQuery } from "./util/getRowsFromQuery";

const PositionsTable = () => {
  const history = useHistory();
  const location = useLocation();
  const annualRefreshEnabled = useComplianceProfileRefreshTab();

  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlArenaIds = urlParams.get("arenaIds");
  const urlEntityId = urlParams.get("entityId");
  const urlPositionIds = urlParams.get("positionIds");

  const [rowData, setRowData] = useState<PositionsTableRowItem[]>([]);
  const [showDashboard, setShowDashboard] = useState(
    Boolean((urlArenaIds && urlEntityId) || urlPositionIds),
  );
  const [positionsGridApi, setPositionsGridApi] =
    useState<AgGrid.GridApi<PositionsTableRowItem>>();
  const [positionsRowsSelected, setPositionsRowsSelected] = useState<PositionsTableRowItem[]>(
    [],
  );

  const { checkComplianceProgram, loading: entityComplianceLoading } =
    useEntityComplianceProgram();
  const [fetchPositions, { loading: positionsLoading }] =
    useGetCompliancePositionsListLazyQuery({
      fetchPolicy: "no-cache",
    });
  const clientLevelPermissionsEnabled = useEnableClientLevelPermissions();
  const { data: scopeData, loading: scopeLoading } = useUserScopedArenaIdsQuery({
    skip: !clientLevelPermissionsEnabled,
  });
  const userScopedArenaIds = scopeData?.userScopedArenaIds ?? null;
  const isLoading = entityComplianceLoading || positionsLoading || scopeLoading;

  const isArenaInScope = useCallback(
    (arenaIds: string[]) =>
      userScopedArenaIds === null ||
      (userScopedArenaIds.length > 0 &&
        arenaIds.every((arenaId) => userScopedArenaIds.includes(arenaId))),
    [userScopedArenaIds],
  );

  const onRowsDeselected = useCallback(() => {
    positionsGridApi?.deselectAll();
  }, [positionsGridApi]);

  const updateUrlParams = useCallback(
    (arenaIds: string[], entityId: string) => {
      if (arenaIds.length === 0) {
        urlParams.delete("arenaIds");
      } else {
        const dbArenaIds = arenaIds.map((id) => String(toDbId(id)));
        urlParams.set("arenaIds", dbArenaIds.join(","));
      }

      if (entityId === "") {
        urlParams.delete("entityId");
      } else {
        urlParams.set("entityId", String(toDbId(entityId)));
      }

      history.replace({
        pathname: location.pathname,
        search: urlParams.toString(),
      });
    },
    [history, location.pathname, urlParams],
  );

  const loadPositionsData = useCallback(
    async (arenaIds: string[], entityId: string) => {
      if (!isArenaInScope(arenaIds)) {
        setShowDashboard(false);
        setRowData([]);
        toast.error("You don't have access to this entity's workspace.");
        updateUrlParams([], "");
        return;
      }
      const response = await fetchPositions({
        variables: {
          positionFilters: {
            arenaIds,
            entityIds: [entityId],
            includeFundAdminEntities: false,
          },
        },
      });
      if (response.data) {
        const positionCount = response.data?.positions?.count ?? 0;
        if (positionCount === 0) {
          setShowDashboard(true);
          setRowData([]);
          toast.error("No positions found for the selected entity.");
          updateUrlParams([], "");
        } else {
          const result = rowsFromQuery(response.data);
          setRowData(result);
          setShowDashboard(true);
          updateUrlParams(arenaIds, entityId);
        }
      } else {
        setShowDashboard(false);
        toast.error(response.error?.message || "Failed to load positions data");
      }
    },
    [fetchPositions, updateUrlParams, isArenaInScope],
  );

  const handleEntitySearchSelect = useCallback(
    async (entityOption: EntitySearchOption) => {
      if (!entityOption.arena || !entityOption.id) return;
      const name = String(entityOption.label ?? entityOption.id);
      const hasProgram = await checkComplianceProgram(String(entityOption.id), name);
      if (!hasProgram) {
        setShowDashboard(false);
        setRowData([]);
        updateUrlParams([], "");
        toast.error("Selected entity must have compliance program enabled.");
        return;
      }
      void loadPositionsData([entityOption.arena.id], String(entityOption.id));
    },
    [checkComplianceProgram, loadPositionsData, updateUrlParams],
  );

  const loadPositionsByIds = useCallback(
    async (positionIds: string[], arenaIds?: string[]) => {
      // Convert database IDs to global IDs for GraphQL query
      const globalPositionIds = positionIds.map((dbId) =>
        toGlobalId("PositionObjectType", dbId),
      );

      // Include arena IDs if provided
      const globalArenaIds = arenaIds
        ? arenaIds.map((id) => toGlobalId("ArenaObjectType", parseInt(id)))
        : [];

      if (globalArenaIds.length > 0 && !isArenaInScope(globalArenaIds)) {
        setShowDashboard(false);
        setRowData([]);
        toast.error("You don't have access to this entity's workspace.");
        return;
      }

      const response = await fetchPositions({
        variables: {
          positionFilters: {
            arenaIds: globalArenaIds,
            positionIds: globalPositionIds,
            includeFundAdminEntities: false,
          },
        },
      });
      if (response.data) {
        const result = rowsFromQuery(response.data);
        setRowData(result);
        setShowDashboard(true);
        if (result.length === 0) {
          toast.error("No positions found for the selected position IDs.");
        }
      } else {
        setShowDashboard(false);
        toast.error(response.error?.message || "Failed to load positions data");
      }
    },
    [fetchPositions, isArenaInScope],
  );

  const gridOptions: AgGrid.GridOptions = {
    rowHeight: 42,
    headerHeight: 48,
    autoSizeStrategy: {
      type: "fitGridWidth",
    },
    rowSelection: "multiple",
    suppressRowClickSelection: true,
    suppressContextMenu: true,
    getRowId: (params: AgGrid.GetRowIdParams) => {
      if (!params.data || typeof params.data !== "object") return "";
      return (params.data as PositionsTableRowItem).id?.toString() ?? "";
    },
  };

  const defaultColDef: AgGrid.ColDef = {
    resizable: true,
    sortable: true,
    filter: true,
    minWidth: 100,
    menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
  };

  useEffect(() => {
    // Wait for the caller's arena scope to resolve before firing any URL-driven
    // position load, otherwise an out-of-scope deep link could briefly load before
    // the gate above has data to check against.
    if (scopeLoading) return;
    if (urlPositionIds) {
      // Handle position IDs filtering
      const positionIdsArray = urlPositionIds.split(",").filter(Boolean);
      if (positionIdsArray.length > 0) {
        // Parse arena IDs if provided
        const arenaIdsForPositions = urlArenaIds
          ? urlArenaIds.split(",").filter(Boolean)
          : undefined;
        void loadPositionsByIds(positionIdsArray, arenaIdsForPositions);
      }
    } else if (urlArenaIds && urlEntityId) {
      // Handle arena/entity filtering
      const arenaIdsArray = urlArenaIds.split(",").filter(Boolean);
      const globalArenaIds = arenaIdsArray.map((id) =>
        toGlobalId("ArenaObjectType", parseInt(id)),
      );
      const globalEntityId = toGlobalId("AccountObjectType", parseInt(urlEntityId));
      void loadPositionsData(globalArenaIds, globalEntityId);
    }
  }, [
    scopeLoading,
    loadPositionsData,
    loadPositionsByIds,
    urlArenaIds,
    urlEntityId,
    urlPositionIds,
  ]);

  const onGridReady = useCallback((event: AgGrid.GridReadyEvent<PositionsTableRowItem>) => {
    setPositionsGridApi(event.api);
    event.api.deselectAll();
  }, []);

  const handleRowSelected = useCallback(
    (event: AgGrid.RowSelectedEvent<PositionsTableRowItem>) => {
      const selectedRows = event.api.getSelectedRows();
      setPositionsRowsSelected(selectedRows);
    },
    [setPositionsRowsSelected],
  );

  const firstDataRendered = useCallback((event: AgGrid.FirstDataRenderedEvent) => {
    event.api.setFilterModel({
      status: {
        filterType: "set",
        values: ["Active"],
      },
    });
    event.api.applyColumnState({
      state: [
        {
          colId: "onboardingStatus",
          sort: "asc",
        },
      ],
    });
  }, []);

  return (
    <Flex className={dashboardContainer}>
      <Flex className={topOptionsContainer}>
        <EntitySearchBox onEntitySelected={handleEntitySearchSelect} />
        <ExportButton
          selectedRows={positionsRowsSelected}
          onRowsDeselected={onRowsDeselected}
        />
      </Flex>
      {isLoading && !showDashboard && <LoadingPage />}
      {showDashboard && (
        <Grid<PositionsTableRowItem>
          columnDefs={getColDefinitions(annualRefreshEnabled)}
          rowData={rowData}
          defaultColDef={defaultColDef}
          overlayLoadingTemplate="Loading positions..."
          gridOptions={gridOptions}
          noRowsOverlayComponent={NoRowsOverlay}
          noRowsOverlayComponentParams={{
            message: "No positions found",
          }}
          onGridReady={onGridReady}
          onFirstDataRendered={firstDataRendered}
          reactiveCustomComponents
          pagination={true}
          paginationPageSize={20}
          paginationPageSizeSelector={false}
          onSelectionChanged={handleRowSelected}
        />
      )}
    </Flex>
  );
};

export default PositionsTable;
